import React, { useEffect, useState } from "react"; // ACCESSIBILITY: added useState for live region
import { useDropzone } from "react-dropzone";

export interface IDragAndDropProps {
  onChange: (img: any) => void;
}

const DragAndDrop: React.FC<IDragAndDropProps> = (props) => {
  let { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone();
  // ACCESSIBILITY: isDragActive added from useDropzone — tells us when a file
  // is being dragged over the zone so we can announce it to screen readers

  // ACCESSIBILITY: live region text — announces drag state and file selection
  // to screen readers without moving focus away from the drop zone
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      props.onChange(acceptedFiles);
    }
  }, [acceptedFiles]);

  // ACCESSIBILITY: announce when a file is dragged over the drop zone
  // so screen reader users know their drag action is being recognised
  useEffect(() => {
    if (isDragActive) {
      setAnnouncement("File detected over drop zone. Release to upload.");
    } else {
      // ACCESSIBILITY: clear the drag announcement when file leaves
      // so it doesn't stay stale in the live region
      setAnnouncement((prev) =>
        prev === "File detected over drop zone. Release to upload." ? "" : prev
      );
    }
  }, [isDragActive]);

  return (
    <div>
      {/* ACCESSIBILITY: visually hidden live region announces drag-and-drop
          state changes to screen readers without moving focus.
          aria-live="assertive" used because file upload feedback is
          time-sensitive and must interrupt current reading immediately */}
      <span
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
      >
        {announcement}
      </span>

      <div
        {...getRootProps({ className: "dropzone" })}
        // ACCESSIBILITY: aria-label describes the drop zone purpose so
        // screen readers announce it when the zone receives focus
        aria-label="Image upload drop zone. Drag and drop an image here or press Enter to browse files."
        // ACCESSIBILITY: aria-dropeffect tells screen readers what happens
        // when a dragged file is dropped here
        aria-dropeffect="copy"
        // ACCESSIBILITY: role="button" makes the drop zone keyboard accessible
        // so users can press Enter to open the file picker — useDropzone
        // already handles this behaviour but role makes it explicit to AT
        role="button"
        // ACCESSIBILITY: tabIndex={0} ensures the drop zone is reachable
        // via Tab key for keyboard-only users who cannot drag and drop
        tabIndex={0}
      >
        <input
          id="appIcon"
          {...getInputProps()}
          className="fileUpload"
          aria-hidden="true"
        />

        <div className="dropZoneOverlay pointer">
          {/* ACCESSIBILITY: aria-hidden hides the visual text from screen
              readers — the parent div's aria-label already provides the
              full description so this text would cause double announcement */}
          <p aria-hidden="true">
            DRAG IMAGE HERE OR <span>BROWSE</span>
          </p>
          <p className="small-text" aria-hidden="true">MAX size 5 MB</p>
        </div>
      </div>
    </div>
  );
};

export default DragAndDrop;